import { phrasesApi } from '@/api'
import { ref } from 'vue'
import { SAPConfiguration } from '@coac-gmbh/saifty-phrases'
import { useAuth } from '@/auth'

const sapConfig = ref<SAPConfiguration[]>([])
const sapConfigLoaded = ref(false)
let configLoadingPromise: Promise<void> | undefined

async function loadConfig() {
  const auth = useAuth()

  try {
    // Wait until the auth state is resolved so we never cache an empty config because the user was not authenticated yet
    await auth.waitUntilLoaded()

    if (!auth?.isAuthenticated) {
      throw new Error('User is not authenticated')
    }

    const { data } = await phrasesApi.getSAPConfigurations()
    sapConfig.value = data
  } catch (error) {
    console.error(error)
  } finally {
    sapConfigLoaded.value = true
  }
}

function ensureConfigLoaded() {
  if (!configLoadingPromise) {
    configLoadingPromise = loadConfig()
  }

  return configLoadingPromise
}

export function useSAPConfig() {
  const auth = useAuth()
  const loadingPromise = ensureConfigLoaded()

  function getConfigByBusinessUnit(businessUnit?: string) {
    if (!businessUnit) return undefined

    return sapConfig.value.find(
      (config) =>
        config.system === businessUnit && config.sap_user_role && auth.user?.roles.includes(config.sap_user_role),
    )
  }

  function waitUntilSAPConfigLoaded(): Promise<void> {
    if (sapConfigLoaded.value) return Promise.resolve()

    return (loadingPromise ?? Promise.resolve()).catch(() => {})
  }

  return { getConfigByBusinessUnit, sapConfig, sapConfigLoaded, waitUntilSAPConfigLoaded }
}
